import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import {
  fetchThreads,
  fetchAvailableModels,
  fetchDefaultPrompts,
  selectThread,
  createThread,
  deleteThread,
  sendMessage,
  clearConversation,
  setDispatcherPrompt,
  setDatakeeperPrompt,
  setSelectedModel,
  runDatakeeper,
  applyBioUpdate,
  updateThread,
  savePromptConfig,
  fetchPromptConfigs,
  fetchSessions,
  saveSession,
  setBottomPanelHeight,
  regenerateMessage,
  fetchProductRecs
} from '../../actions/playgroundActions';
import ThreadsList from './ThreadsList';
import PlaygroundChat from './PlaygroundChat';
import DispatcherBrain from './DispatcherBrain';
import PromptEditors from './PromptEditors';
import GradeModal from './GradeModal';
import './Playground.scss';

const MIN_HEIGHT = 120;

const Playground = props => {
  const {
    playground,
    fetchThreads,
    fetchAvailableModels,
    fetchDefaultPrompts,
    selectThread,
    createThread,
    deleteThread,
    sendMessage,
    clearConversation,
    setDispatcherPrompt,
    setDatakeeperPrompt,
    setSelectedModel,
    runDatakeeper,
    applyBioUpdate,
    updateThread,
    savePromptConfig,
    fetchPromptConfigs,
    fetchSessions,
    saveSession,
    setBottomPanelHeight,
    regenerateMessage,
    fetchProductRecs
  } = props;

  const {
    threads,
    selectedThread,
    messages,
    brief,
    briefComplete,
    isLoading,
    llmContext,
    inlineDispatcherPrompt,
    inlineDatakeeperPrompt,
    selectedModel,
    availableModels,
    datakeeperResult,
    datakeeperLoading,
    promptConfigs,
    lastSavedSession,
    sessions,
    sessionsLoading,
    bottomPanelHeight,
    productRecs: rawProductRecs,
    productRecsLoading
  } = playground;

  const productRecs = Array.isArray(rawProductRecs) ? rawProductRecs : [];

  const [showGradeModal, setShowGradeModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeConfigName, setActiveConfigName] = useState(null);

  const dragRef = useRef({ startY: 0, startHeight: 0, active: false });
  const bottomPanelRef = useRef(null);
  const dispatchHeightRef = useRef(setBottomPanelHeight);
  dispatchHeightRef.current = setBottomPanelHeight;

  useEffect(() => {
    fetchThreads();
    fetchAvailableModels();
    fetchDefaultPrompts();
  }, []);

  useEffect(() => {
    if (brief) {
      fetchProductRecs(brief);
    }
  }, [brief]);

  useEffect(() => {
    const onMouseMove = e => {
      if (!dragRef.current.active) return;
      const delta = dragRef.current.startY - e.clientY;
      const maxHeight = window.innerHeight * 0.6;
      const newHeight = Math.min(maxHeight, Math.max(MIN_HEIGHT, dragRef.current.startHeight + delta));
      dispatchHeightRef.current(newHeight);
    };
    const onMouseUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      if (bottomPanelRef.current) bottomPanelRef.current.classList.remove('resizing');
      setIsDragging(false);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleMouseDown = e => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startHeight: bottomPanelHeight || 280, active: true };
    // Add resizing class immediately via DOM to disable CSS transition before React re-renders
    if (bottomPanelRef.current) bottomPanelRef.current.classList.add('resizing');
    setIsDragging(true);
  };

  const handleDeleteThread = async id => {
    try {
      await deleteThread(id);
    } catch (error) {
      cogoToast.error('Failed to delete thread');
    }
  };

  const handleSendMessage = async message => {
    try {
      await sendMessage({
        threadId: selectedThread?.id,
        message,
        dispatcherPrompt: inlineDispatcherPrompt,
        model: selectedModel
      });
    } catch (error) {
      cogoToast.error('Failed to send message');
    }
  };

  const handleRegenerate = async messageIndex => {
    try {
      await regenerateMessage({
        messageIndex,
        threadId: selectedThread?.id,
        dispatcherPrompt: inlineDispatcherPrompt,
        model: selectedModel
      });
    } catch (error) {
      cogoToast.error('Failed to regenerate');
    }
  };

  const handleRunDatakeeper = async () => {
    try {
      await runDatakeeper({
        threadId: selectedThread?.id,
        brief,
        datakeeperPrompt: inlineDatakeeperPrompt,
        model: selectedModel
      });
      cogoToast.success('Datakeeper analysis complete');
    } catch (error) {
      cogoToast.error('Datakeeper failed');
    }
  };

  const handleApplyBio = async newBio => {
    try {
      await applyBioUpdate({ threadId: selectedThread.id, newBio });
      cogoToast.success('Bio updated');
    } catch (error) {
      cogoToast.error('Failed to update bio');
    }
  };

  const handleSaveSession = async ({ humanGrade, humanNotes, isGoldenDataset }) => {
    try {
      await saveSession({
        threadId: selectedThread?.id,
        humanGrade,
        humanNotes,
        isGoldenDataset
      });
      cogoToast.success(isGoldenDataset ? 'Saved to golden dataset' : 'Session saved');
      setShowGradeModal(false);
    } catch (error) {
      cogoToast.error('Failed to save session');
    }
  };

  const handleClearConversation = () => {
    clearConversation(selectedThread?.id);
  };

  return (
    <div className='playground-page'>
      <div className='playground-main'>
        <div className='playground-left'>
          <ThreadsList
            threads={threads || []}
            selectedThread={selectedThread}
            onSelect={selectThread}
            onCreate={createThread}
            onDelete={handleDeleteThread}
          />
        </div>
        <div className='playground-center'>
          <PlaygroundChat
            selectedThread={selectedThread}
            messages={messages}
            briefComplete={briefComplete}
            isLoading={isLoading}
            datakeeperLoading={datakeeperLoading}
            onSend={handleSendMessage}
            onReset={handleClearConversation}
            onRegenerate={handleRegenerate}
            onRunDatakeeper={handleRunDatakeeper}
            onGrade={() => setShowGradeModal(true)}
          />
        </div>
        <div className='playground-right'>
          <DispatcherBrain
            selectedThread={selectedThread}
            brief={brief}
            briefComplete={briefComplete}
            datakeeperResult={datakeeperResult}
            datakeeperLoading={datakeeperLoading}
            sessions={sessions}
            sessionsLoading={sessionsLoading}
            productRecs={productRecs}
            productRecsLoading={productRecsLoading}
            onFetchProductRecs={fetchProductRecs}
            onApplyBio={handleApplyBio}
            onUpdateBio={(id, bio) => updateThread(id, { testShopperBio: bio })}
            onFetchSessions={fetchSessions}
          />
        </div>
      </div>
      <div className='resize-handle' onMouseDown={handleMouseDown} />
      <div ref={bottomPanelRef} className={cn('playground-bottom', { resizing: isDragging })} style={{ height: bottomPanelHeight }}>
        <PromptEditors
          dispatcherPrompt={inlineDispatcherPrompt}
          datakeeperPrompt={inlineDatakeeperPrompt}
          selectedModel={selectedModel}
          availableModels={availableModels}
          promptConfigs={promptConfigs}
          onDispatcherChange={setDispatcherPrompt}
          onDatakeeperChange={setDatakeeperPrompt}
          onModelChange={setSelectedModel}
          onSaveConfig={savePromptConfig}
          onFetchConfigs={fetchPromptConfigs}
          onConfigChange={setActiveConfigName}
          onRestoreDefault={() => fetchDefaultPrompts()}
        />
      </div>
      {showGradeModal && (
        <GradeModal
          messages={messages}
          selectedThread={selectedThread}
          selectedModel={selectedModel}
          llmContext={llmContext}
          onSave={handleSaveSession}
          onClose={() => setShowGradeModal(false)}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const { playground } = state;
  return { playground };
};

export default connect(mapStateToProps, {
  fetchThreads,
  fetchAvailableModels,
  fetchDefaultPrompts,
  selectThread,
  createThread,
  deleteThread,
  sendMessage,
  clearConversation,
  setDispatcherPrompt,
  setDatakeeperPrompt,
  setSelectedModel,
  runDatakeeper,
  applyBioUpdate,
  updateThread,
  savePromptConfig,
  fetchPromptConfigs,
  fetchSessions,
  saveSession,
  setBottomPanelHeight,
  regenerateMessage,
  fetchProductRecs
})(Playground);
